/**
 * A screen of this design shown as a popup over the page — what an Open popup
 * button with "A screen I design" content opens.
 *
 * The screen is an ordinary root frame, designed on the canvas with any
 * layers and components, so it is live here exactly as on the page: inputs,
 * uploads and buttons all run through the same `DesignRuntimeService`. A
 * Close popup button inside it, Esc, the ✕ or a backdrop click closes it.
 *
 * In the builder (`editable`) the open popup can be dragged by its grip to
 * where it should appear, and resized from its corner. The spot is saved on
 * the button (`popupPlaceX/Y`), so it opens there every time — published too;
 * a table's cell button keeps it on its table (`appearance.popupPlacements`).
 * The size is the frame's own, so its content rescales with it.
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { buttonConfigOf } from '../../../global-components/dvm-action-button/dvm-action-button.types';
import type { DesignNode } from '../design-builder.types';
import { DesignRuntimeService } from '../data/design-runtime.service';
import { widgetNodes } from '../data/design-tiles.utils';
import { DesignDocumentStore } from '../state/design-document.store';
import { findNode } from '../utils/design-node.utils';
import { screensOf } from '../utils/design-publish.utils';
import { DesignScreenPreviewComponent } from './design-screen-preview.component';

/** Space kept between the popup and the window edge, in px. */
const EDGE = 24;
/** Smallest popup box a corner drag can make, in px. */
const MIN_SIZE = 120;

type Gesture =
  | { kind: 'move'; startX: number; startY: number; left: number; top: number }
  | { kind: 'resize'; startX: number; startY: number; frame: DesignNode };

@Component({
  selector: 'app-design-screen-popup',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, DesignScreenPreviewComponent],
  templateUrl: './design-screen-popup.component.html',
  styleUrl: './design-screen-popup.component.scss',
})
export class DesignScreenPopupComponent implements OnDestroy {
  /** Builder: the popup can be moved and resized, and the result is saved. */
  readonly editable = input(false);

  private readonly runtime = inject(DesignRuntimeService);
  private readonly doc = inject(DesignDocumentStore);

  /** The open popup's screen, live with the document; null = closed. */
  readonly screen = computed(() => {
    const request = this.runtime.popupScreen();
    if (!request) return null;
    this.doc.revision();
    return screensOf(this.doc.document()).find((screen) => screen.id === request.screenId) ?? null;
  });

  /**
   * Where the placement is stored: the Button widget that opened it (its appearance), or
   * for a button in a table cell - opened as `<table node>:...:<button id>` - the table,
   * under `appearance.popupPlacements[<the rest>]`.
   */
  private readonly owner = computed((): { node: DesignNode; key: string | null } | null => {
    const request = this.runtime.popupScreen();
    if (!request) return null;
    const widgets = widgetNodes(this.doc.document());
    const button = widgets.find((node) => node.id === request.buttonId);
    if (button?.widget) return { node: button, key: null };
    const [nodeId, ...rest] = request.buttonId.split(':');
    const table = rest.length ? widgets.find((node) => node.id === nodeId) : undefined;
    return table?.widget ? { node: table, key: rest.join(':') } : null;
  });

  /** The saved spot, as shares of the free space (-1 = centred). */
  private readonly placement = computed((): { x: number; y: number } => {
    const owner = this.owner();
    const appearance = owner?.node.widget?.appearance ?? {};
    if (!owner || owner.key === null) {
      const config = buttonConfigOf(appearance);
      return { x: Number(config.popupPlaceX), y: Number(config.popupPlaceY) };
    }
    const spot = (appearance['popupPlacements'] as Record<string, Record<string, unknown>> | undefined)?.[owner.key];
    return { x: Number(spot?.['popupPlaceX'] ?? -1), y: Number(spot?.['popupPlaceY'] ?? -1) };
  });

  private readonly viewport = signal({ width: window.innerWidth, height: window.innerHeight });
  /** Live position while dragging; null = use the saved placement. */
  private readonly dragPosition = signal<{ left: number; top: number } | null>(null);
  private gesture: Gesture | null = null;
  readonly dragging = signal(false);

  /** On-screen box: the frame's size, capped by the window. */
  private readonly box = computed(() => {
    const screen = this.screen();
    const { width, height } = this.viewport();
    return {
      width: Math.min(screen?.width ?? 0, Math.max(MIN_SIZE, width - EDGE * 2)),
      height: Math.min(screen?.height ?? 0, Math.max(MIN_SIZE, height - EDGE * 2)),
    };
  });

  /** Top-left in px: dragged, saved (share of the free space), or centred. */
  private readonly position = computed(() => {
    const dragged = this.dragPosition();
    if (dragged) return dragged;
    const spot = this.placement();
    const { width, height } = this.viewport();
    const box = this.box();
    const place = (share: number, free: number): number =>
      share >= 0 && share <= 1 ? EDGE + share * Math.max(0, free - EDGE * 2) : free / 2;
    return {
      left: Math.round(place(spot.x, width - box.width)),
      top: Math.round(place(spot.y, height - box.height)),
    };
  });

  readonly placed = computed(() => {
    const spot = this.placement();
    return spot.x >= 0 || spot.y >= 0;
  });

  readonly dialogStyles = computed((): Record<string, string> => {
    const screen = this.screen();
    if (!screen) return {};
    const box = this.box();
    const position = this.position();
    return {
      width: `${box.width}px`,
      height: `${box.height}px`,
      left: `${position.left}px`,
      top: `${position.top}px`,
      background: screen.background || '#ffffff',
    };
  });

  private readonly overlay = viewChild<ElementRef<HTMLElement>>('overlay');

  constructor() {
    // position:fixed resolves against the nearest transformed ancestor, which
    // would trap the overlay inside the canvas — move it to <body> once shown.
    effect(() => {
      const element = this.overlay()?.nativeElement;
      if (element && element.parentElement !== document.body) document.body.appendChild(element);
    });
  }

  close(): void {
    this.runtime.closePopupScreen();
  }

  onBackdropClick(): void {
    // The click that ends a drag lands on the backdrop — never close on it.
    if (!this.dragging()) this.close();
  }

  // ── Move & resize (builder only) ───────────────────────────────────────────

  startMove(event: PointerEvent): void {
    if (!this.editable() || event.button !== 0) return;
    const position = this.position();
    this.begin(event, { kind: 'move', startX: event.clientX, startY: event.clientY, ...position });
  }

  startResize(event: PointerEvent): void {
    const screen = this.screen();
    const frame = screen ? findNode(this.doc.nodes(), screen.id) : null;
    if (!this.editable() || event.button !== 0 || !frame) return;
    this.begin(event, { kind: 'resize', startX: event.clientX, startY: event.clientY, frame: structuredClone(frame) });
  }

  private begin(event: PointerEvent, gesture: Gesture): void {
    event.preventDefault();
    event.stopPropagation();
    this.gesture = gesture;
    this.dragging.set(true);
  }

  // Window-level, so a fast drag that leaves the grip keeps going.
  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const gesture = this.gesture;
    if (!gesture) return;
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    if (gesture.kind === 'move') {
      const { width, height } = this.viewport();
      const box = this.box();
      this.dragPosition.set({
        left: Math.round(clamp(gesture.left + dx, 0, Math.max(0, width - box.width))),
        top: Math.round(clamp(gesture.top + dy, 0, Math.max(0, height - box.height))),
      });
      return;
    }
    // Resize the frame itself: its content rescales with it (see the store).
    const frame = gesture.frame;
    this.doc.resizeNode(frame.id, {
      x: frame.x,
      y: frame.y,
      width: Math.max(MIN_SIZE, frame.width + dx),
      height: Math.max(MIN_SIZE, frame.height + dy),
    }, frame, 'Resize popup');
  }

  @HostListener('window:pointerup')
  @HostListener('window:pointercancel')
  onPointerUp(): void {
    const gesture = this.gesture;
    if (!gesture) return;
    this.gesture = null;
    if (gesture.kind === 'move') this.savePlacement();
    // Let the click that follows this pointerup pass before closing is allowed.
    setTimeout(() => this.dragging.set(false));
  }

  /** Store the dragged spot on the button as a share of the free space. */
  private savePlacement(): void {
    const dragged = this.dragPosition();
    this.dragPosition.set(null);
    if (!dragged) return;
    const { width, height } = this.viewport();
    const box = this.box();
    const share = (value: number, free: number): number =>
      free - EDGE * 2 > 0 ? round3(clamp((value - EDGE) / (free - EDGE * 2), 0, 1)) : 0.5;
    this.writePlacement(share(dragged.left, width - box.width), share(dragged.top, height - box.height), 'Place popup');
  }

  /** Back to opening in the middle of the window. */
  centre(): void {
    this.writePlacement(-1, -1, 'Centre popup');
  }

  private writePlacement(x: number, y: number, label: string): void {
    const owner = this.owner();
    const widget = owner?.node.widget;
    if (!owner || !widget) return;
    const appearance = widget.appearance ?? {};
    const spot = { popupPlaceX: x, popupPlaceY: y };
    const next = owner.key === null
      ? { ...appearance, ...spot }
      : { ...appearance, popupPlacements: { ...((appearance['popupPlacements'] as Record<string, unknown> | undefined) ?? {}), [owner.key]: spot } };
    this.doc.updateNode(owner.node.id, { widget: { ...widget, appearance: next } }, label);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewport.set({ width: window.innerWidth, height: window.innerHeight });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.screen()) this.close();
  }

  ngOnDestroy(): void {
    const element = this.overlay()?.nativeElement;
    if (element && element.parentElement === document.body) element.remove();
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
